import React, { useRef } from 'react'

import { formatDistanceToNow } from 'date-fns'
import { EditorState, LexicalEditor } from 'lexical'
import _debounce from 'lodash/debounce'

import { Avatar } from './Avatar'
import { MaxEditor, getEditorValueAstHtml, updateEditor } from '@/components/MaxEditor'
import { getMentions } from '@/components/MaxEditor/utils'
import { useCreateNoteMutation, useUpdateNoteMutation } from '@/services/notes'
import { useGetStaffUsersQuery } from '@/services/user'
import { Note } from '@/types/unlock'

type DealNote = {
  title: string
  dealNote: Note
  userDraftKey: string
  template?: string
}

export function DealNote({ title, dealNote, userDraftKey, template = '' }: DealNote) {
  const editorRef = useRef<LexicalEditor | null>(null)
  const { data: users } = useGetStaffUsersQuery()

  const [isEdit, setIsEdit] = React.useState(() => {
    const draft = window.sessionStorage.getItem(userDraftKey)
    return draft ? true : false
  })

  const [createNote] = useCreateNoteMutation()
  const [updateNote] = useUpdateNoteMutation()

  const clearSessionStorage = () => window.sessionStorage.removeItem(userDraftKey)

  const onEditorChange = _debounce((editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = getEditorValueAstHtml(editor)
      if (html) {
        // Drafts are saved in sessionStorage
        // using editorState format for simpler deserialization
        window.sessionStorage.setItem(userDraftKey, JSON.stringify(editorState))
      }
    })
  }, 300)

  const onCancel = () => {
    setIsEdit(false)
    clearSessionStorage()
  }

  const onSave = () => {
    if (isEdit) {
      editorRef.current.getEditorState().read(async () => {
        const payload: Partial<Note> = {
          application: dealNote?.application,
          type: dealNote?.type,
        }

        const mentionedUsers = getMentions(editorRef.current)
        if (mentionedUsers?.length > 0) {
          payload['tagged_users'] = mentionedUsers.map((user) => user.id)
        }

        const html = getEditorValueAstHtml(editorRef.current)
        if (html) {
          payload.content = html
          if (dealNote?.id) {
            await updateNote({ uuid: dealNote.uuid, ...payload })
          } else {
            await createNote(payload)
          }
          setIsEdit(false)
          clearSessionStorage()
        }
      })
    } else {
      setIsEdit(true)
      setTimeout(() => editorRef?.current?.focus(), 100)
    }
  }

  return (
    <li className='card shadow-sm border'>
      <div className='card-body p-5'>
        <div className='card-title text-base text-indigo-700'>
          {title}
          {isEdit && <div className='badge badge-sm font-normal'>draft</div>}
        </div>
        {dealNote?.content && !isEdit && (
          <>
            {dealNote?.author && <Avatar user={dealNote.author} />}
            <p
              className='prose prose-sm mb-3 prose-p:my-1'
              dangerouslySetInnerHTML={{ __html: dealNote?.content }}
            />
            <div className='flex flex-col text-gray-400 hover:text-gray-600 text-sm cursor-pointer'>
              <small>
                Created:{' '}
                {formatDistanceToNow(new Date(dealNote.created_at), {
                  addSuffix: true,
                })}
              </small>
              <small>
                Last edit:{' '}
                {formatDistanceToNow(new Date(dealNote.modified_at), {
                  addSuffix: true,
                })}
              </small>
            </div>
          </>
        )}
        {isEdit && (
          <div className='bg-base-100'>
            <MaxEditor
              editorRef={editorRef}
              customInitialConfig={{
                namespace: 'deal-notes-editor',
                editorState(editor) {
                  const draft = window.sessionStorage.getItem(userDraftKey)
                  if (draft && draft !== '') {
                    const stateFromDraft = editor.parseEditorState(draft)
                    return editor.setEditorState(stateFromDraft)
                  }
                  updateEditor(editor, dealNote?.content ?? template)
                },
              }}
              onChange={onEditorChange}
              textSize='sm'
              mentions={{
                items: {
                  '@': users?.map((user) => ({
                    id: user.data.id, // Matches the Id used in Streaming Notes
                    value: `${user.first_name} ${user.last_name}`.trim(),
                    username: user.username,
                  })),
                },
              }}
            />
          </div>
        )}
        <div className='card-actions justify-end'>
          {isEdit && (
            <button
              type='button'
              className='btn btn-sm btn-ghost'
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
          <button
            className='btn btn-sm btn-outline btn-primary'
            type='button'
            onClick={onSave}
          >
            {isEdit ? 'Save' : 'Edit'}
          </button>
        </div>
      </div>
    </li>
  )
}
